import {
  IOfferingSummaryResSchema,
  IQuotePrice,
  IQuotePriceScheduleProductPrice,
  OfferingTypesEnum,
  ProductTypeEnum,
  TQuotePrice,
} from '@monetize/types/app';
import {
  formatCurrency,
  formatInteger,
  sortByProductPosition,
  toDateShort,
} from '@monetize/utils/core';
import { Fragment } from 'react';
import {
  MBox,
  MFlex,
  MSkeleton,
  MText,
} from '../../../../../components/Monetize';
import { MPercent } from '../../../../../components/Monetize/MPercent';
import MRowWithLineSeparator from '../../../../../components/Monetize/MRowWithLineSeparator';
import { PRODUCT_TYPE_DISPLAY_SHORT } from '../../../../../constants/products';
import { getPriceDisplayWithFrequency } from '../../../../../utils/quotes';

const LoadingSkeletons = () => (
  <MBox mt={2}>
    <MSkeleton height="5" width="30%" mb="2" />
    <MSkeleton height="3" width="20%" mb="2" />
    <MSkeleton height="3" width="100%" mb="2" />
  </MBox>
);
const ProductPriceRow = ({
  productPrice,
  productType,
  priceFrequency,
  billingFrequency,
  isPoTOfferingType,
  currency,
}: {
  productPrice: TQuotePrice;
  productType: ProductTypeEnum;
  priceFrequency?: number;
  billingFrequency: number;
  isPoTOfferingType: boolean;
  currency: string;
}) => {
  const hasDiscount =
    !!productPrice.displayAmount &&
    productPrice.displayAmount !== productPrice.displayNet;
  return (
    <MFlex flexDir="row" align="center" justify="space-between">
      <MRowWithLineSeparator
        my={0.5}
        lineProps={{
          background: 'tBlue.lightShade',
          mt: hasDiscount ? '4' : '0',
        }}
        leftLabel={
          <MText mt={hasDiscount ? '4' : '0'}>
            {formatInteger(productPrice.from)} -{' '}
            {productPrice.to ? formatInteger(productPrice.to) : '∞'}
          </MText>
        }
        rightLabel={
          <MFlex>
            {isPoTOfferingType ? (
              <>
                <MPercent amount={productPrice.net} />
                <MText ml="1" as="span">
                  of other products
                </MText>
              </>
            ) : (
              <MBox textAlign="right">
                {hasDiscount && (
                  <MText textDecoration="line-through" fontSize="xs">
                    {getPriceDisplayWithFrequency({
                      price: formatCurrency(productPrice.displayAmount, {
                        currency,
                        maximumFractionDigits: 'max',
                      }),
                      productType,
                      priceFrequency,
                      billingFrequency,
                      pricingModel: productPrice.priceModel,
                    })}
                  </MText>
                )}
                <MText>
                  {getPriceDisplayWithFrequency({
                    price: formatCurrency(productPrice.displayNet, {
                      currency,
                      maximumFractionDigits: 'max',
                    }),
                    productType,
                    priceFrequency,
                    billingFrequency,
                    pricingModel: productPrice.priceModel,
                  })}
                </MText>
              </MBox>
            )}
          </MFlex>
        }
      />
    </MFlex>
  );
};

const ProductPrices = ({
  prices,
  productType,
  priceFrequency,
  billingFrequency,
  isPoTOfferingType,
  currency,
}: {
  prices: TQuotePrice[];
  productType: ProductTypeEnum;
  priceFrequency?: number;
  billingFrequency: number;
  isPoTOfferingType: boolean;
  currency: string;
}) => (
  <>
    {prices.map((productPrice, productPriceIndex) => (
      <ProductPriceRow
        productPrice={productPrice}
        productType={productType}
        priceFrequency={priceFrequency}
        billingFrequency={billingFrequency}
        isPoTOfferingType={isPoTOfferingType}
        currency={currency}
        key={productPriceIndex}
      />
    ))}
  </>
);

const ProductPriceSection = ({
  productPrices,
  isPoTOfferingType,
  currency,
  billingFrequency,
}: {
  productPrices: IQuotePriceScheduleProductPrice[];
  isPoTOfferingType: boolean;
  currency: string;
  billingFrequency: number;
}) => {
  const sortedPrices = sortByProductPosition(productPrices);

  return (
    <>
      {sortedPrices.map((price, priceIndex) => (
        <MBox key={priceIndex}>
          <MFlex flexDir="row" align="center" justify="space-between">
            <MRowWithLineSeparator
              my={0.5}
              lineProps={{ background: 'transparent' }}
              leftLabel={
                <MFlex width="100%">
                  <MText fontWeight="bold" fontStyle="italic">
                    {price.product.name}&nbsp;
                  </MText>
                  <MText fontWeight="medium" fontStyle="italic">
                    ({PRODUCT_TYPE_DISPLAY_SHORT[price.product.productType]})
                  </MText>
                </MFlex>
              }
              rightLabel={null}
            />
          </MFlex>
          <ProductPrices
            prices={price.prices}
            productType={price.product.productType}
            priceFrequency={price.displayUnitPriceFrequency}
            billingFrequency={billingFrequency}
            isPoTOfferingType={isPoTOfferingType}
            currency={currency}
          />
        </MBox>
      ))}
    </>
  );
};

const QuotePriceSchedules = ({
  quotePrice,
  isPoTOfferingType,
  currency,
}: {
  quotePrice: IQuotePrice;
  isPoTOfferingType: boolean;
  currency: string;
}) => (
  <>
    {quotePrice.schedule.map((schedule) => (
      <Fragment key={schedule.quoteOfferingId}>
        <MFlex alignItems="center" mt="2">
          <MText fontWeight="bold" fontSize="md">
            {quotePrice.offering.name}{' '}
            {schedule.rate?.name ? `- ${schedule.rate.name}` : ''}
          </MText>
          <MText color="tGray.acGray" fontWeight="normal" fontSize="xs" ml={2}>
            {toDateShort(schedule.startDate)} - {toDateShort(schedule.endDate)}
          </MText>
        </MFlex>
        <ProductPriceSection
          productPrices={schedule.productPrices}
          isPoTOfferingType={isPoTOfferingType}
          currency={currency}
          billingFrequency={schedule.rate.billingFrequencyInMonths}
        />
      </Fragment>
    ))}
  </>
);

const PricesSectionContent = ({
  quotePrices,
  loading,
  currency,
  offeringsById = {},
}: {
  quotePrices: IQuotePrice[];
  loading: boolean;
  currency: string;
  offeringsById?: Record<string, IOfferingSummaryResSchema>;
}) => {
  if (loading) {
    return <LoadingSkeletons />;
  }

  return (
    <>
      {quotePrices.map((quotePrice) => {
        const offering = offeringsById[quotePrice.offering.id];
        const isPoTOfferingType =
          offering?.type === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL;

        return (
          <QuotePriceSchedules
            key={quotePrice.quoteOfferingId}
            quotePrice={quotePrice}
            isPoTOfferingType={isPoTOfferingType}
            currency={currency}
          />
        );
      })}
    </>
  );
};

export default PricesSectionContent;
